import { useState, useCallback, useMemo, useRef } from 'react';
import { useAuthManager, useCurrentOrgId, useCurrentUser } from '../auth';

interface GlobalLoadingState {
  isAuthLoading: boolean;
  isOrgLoading: boolean;
  isEmailLoading: boolean;
  isUserLoading: boolean;
  isAnyLoading: boolean;
  areAllCriticalFetchesComplete: boolean;
}

/**
 * Centralized loading state management
 * Tracks all critical fetches needed before app is ready
 */
export const useGlobalLoading = () => {
  const { loading: authLoading, hasConnectedAccount } = useAuthManager();
  const { loading: orgLoading, orgId } = useCurrentOrgId();
  const { loading: userLoading } = useCurrentUser();
  const [emailLoading, setEmailLoading] = useState(false);

  const setEmailLoadingState = useCallback((loading: boolean) => {
    setEmailLoading(loading);
  }, []);

  // Critical fetches that must complete before app is ready:
  // 1. Authentication (session)
  // 2. Organization (orgId)
  // 3. User profile (user data)
  // Email loading is NOT critical for app initialization — it loads independently after mount.
  //
  // LATCH: Once all critical fetches complete, this stays true permanently.
  // This prevents transient loading flickers (e.g., useAuthManager briefly setting loading=true
  // during a re-fetch) from cascading up through useAppInitialization → AppContent and
  // potentially causing child unmounts. Sign-out triggers page reload, so latching is safe.
  const rawComplete = !authLoading && !orgLoading && !userLoading;
  const hasEverCompletedRef = useRef(false);
  if (rawComplete) {
    hasEverCompletedRef.current = true;
  }
  const areAllCriticalFetchesComplete = hasEverCompletedRef.current;

  return useMemo(() => ({
    isAuthLoading: authLoading,
    isOrgLoading: orgLoading,
    isEmailLoading: emailLoading,
    isUserLoading: userLoading,
    isAnyLoading: authLoading || orgLoading || emailLoading || userLoading,
    areAllCriticalFetchesComplete,
    setEmailLoading: setEmailLoadingState
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [authLoading, orgLoading, emailLoading, userLoading, rawComplete, setEmailLoadingState]);
};

